// Annuaire des codes postaux Eurêkali (19/10/2026, demande du CEO).
// Le client tape « Paris » : la liste 75001 Paris … 75020 Paris s'affiche ; « 750 » :
// les codes qui commencent par 750. Données GeoNames (CC BY 4.0), servies par notre
// moteur : aucun appel Google, aucune panne Google possible.
import axios from 'axios'
import store from './store'

// Pays où l'annuaire a le code complet (même liste que PAYS_SERVIS côté serveur).
// Royaume-Uni et Pays-Bas n'y sont pas : GeoNames n'a que le début du code.
export const PAYS_ANNUAIRE = ['be', 'lu', 'fr', 'de', 'ch', 'dk']

// Licence CC BY 4.0 : auteur, licence, lien, et mention que les données sont filtrées.
export const MENTION_CODES_POSTAUX = {
  texte: 'Codes postaux © GeoNames.org (CC BY 4.0), filtrés',
  lien: 'https://creativecommons.org/licenses/by/4.0/',
}

// Forme d'un code postal complet, par pays de l'express (départ Belgique), et un exemple.
// Luxembourg : « 1111 » et non « L-1111 », comme 43 des 50 envois B2C vers LU.
// Aucun code ne commence par 0 en BE, LU, CH, NL, ni par 00 en FR et DE.
const FORMES = {
  be: { forme: /^[1-9]\d{3}$/, exemple: '1000' },
  lu: { forme: /^[1-9]\d{3}$/, exemple: '1111' },
  fr: { forme: /^(?!00)\d{5}$/, exemple: '75001' },
  de: { forme: /^(?!00)\d{5}$/, exemple: '10115' },
  ch: { forme: /^[1-9]\d{3}$/, exemple: '8001' },
  dk: { forme: /^\d{4}$/, exemple: '1050' },
  gb: { forme: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/, exemple: 'SW1A 1AA' },
  nl: { forme: /^[1-9]\d{3} [A-Z]{2}$/, exemple: '1012 AB' },
}

const MESSAGES = {
  choisir: {
    fr: () => 'Choisissez votre code postal dans la liste.',
    en: () => 'Please choose your postcode from the list.',
    nl: () => 'Kies uw postcode in de lijst.',
  },
  complet: {
    fr: (ex) => `Tapez le code postal complet (exemple : ${ex}).`,
    en: (ex) => `Please type the full postcode (example: ${ex}).`,
    nl: (ex) => `Typ de volledige postcode (voorbeeld: ${ex}).`,
  },
  plus: {
    fr: () => '… et d’autres : tapez le début du code postal',
    en: () => '… and more: type the first digits of the postcode',
    nl: () => '… en meer: typ de eerste cijfers van de postcode',
  },
}

// Destination sans pays (19/10/2026) : chaque ligne dit son pays, et « id » est la valeur
// de la liste des pays de l'écran (« France », « Germany »…), que le choix remplit.
const PAYS = {
  BE: { id: 'Belgium', fr: 'Belgique', en: 'Belgium', nl: 'België' },
  LU: { id: 'Luxembourg', fr: 'Luxembourg', en: 'Luxembourg', nl: 'Luxemburg' },
  FR: { id: 'France', fr: 'France', en: 'France', nl: 'Frankrijk' },
  DE: { id: 'Germany', fr: 'Allemagne', en: 'Germany', nl: 'Duitsland' },
  CH: { id: 'Switzerland', fr: 'Suisse', en: 'Switzerland', nl: 'Zwitserland' },
  DK: { id: 'Denmark', fr: 'Danemark', en: 'Denmark', nl: 'Denemarken' },
}

function langue() {
  return (store && store.state && store.state.lang) || 'fr'
}

function message(cle, exemple) {
  return (MESSAGES[cle][langue()] || MESSAGES[cle].fr)(exemple)
}

// « 75001 Paris » → « 75001 », « 1000-Bruxelles » → « 1000 », « B-1000 » → « 1000 »,
// « sw1a1aa » → « SW1A 1AA », « Amsterdam, 1012 AB » → « 1012 AB ».
function auPropre(texte, pays) {
  let t = texte.trim().toUpperCase().replace(/\s+/g, ' ')
  // Royaume-Uni et Pays-Bas : le code est reconnaissable où qu'il soit dans le texte.
  if (pays === 'gb') {
    const m = t.match(/(?:^|[^A-Z\d])([A-Z]{1,2}\d[A-Z\d]?) ?(\d[A-Z]{2})(?![A-Z\d])/)
    return m ? `${m[1]} ${m[2]}` : t
  }
  if (pays === 'nl') {
    const m = t.match(/(?:^|\D)(\d{4}) ?([A-Z]{2})(?![A-Z])/)
    return m ? `${m[1]} ${m[2]}` : t
  }
  t = t.replace(/^[A-Z]{1,2} ?-? ?(?=\d)/, '') // lettres du pays devant le code
  const m = t.match(/^(\d+)(?:(?: ?[-,] ?| )\p{L}.*)?$/u) // le code, suivi ou non de la ville (« 1000-1200 » : refusé)
  return m ? m[1] : t
}

// Vérifie le code postal quand le client quitte le champ (option « verifier » d'AutocompletionEk).
// null : accepté tel quel ; { code } : remis au propre ; { message } : refusé, le champ est vidé.
// Un code bien formé mais absent de l'annuaire reste accepté : CEDEX, codes d'entreprises
// et codes récents sont de vrais codes, que l'annuaire ne propose simplement pas.
export function verifierCodePostal(texte, regions, listeMontree) {
  const pays = (regions && regions[0]) || ''
  const regle = FORMES[pays]
  if (!regle) return /\d/.test(texte) ? null : { message: message('choisir') }
  const code = auPropre(texte, pays)
  if (regle.forme.test(code)) return code === texte ? null : { code }
  return { message: listeMontree ? message('choisir') : message('complet', regle.exemple) }
}

// Transforme une réponse du moteur en suggestions lues par les écrans
// (mêmes « address_components » que l'ancien widget Google : postal_code, locality).
// plus : la liste est coupée ; une dernière ligne, non choisissable, le dit.
export function versSuggestions(resultats, plus = false) {
  const suggestions = (resultats || []).map((r) => {
    const pays = r.pays && PAYS[r.pays]
    const composants = [
      { long_name: r.code, short_name: r.code, types: ['postal_code'] },
      { long_name: r.ville, short_name: r.ville, types: ['locality', 'political'] },
    ]
    if (pays) composants.push({ long_name: pays.id, short_name: r.pays, types: ['country', 'political'] })
    return {
      texte: pays ? `${r.code} ${r.ville} — ${pays[langue()] || pays.fr}` : `${r.code} ${r.ville}`,
      composants,
    }
  })
  if (plus && suggestions.length) suggestions.push({ texte: message('plus'), info: true })
  return suggestions
}

async function chercher(pays, texte, client) {
  const reponse = await client.get(`/api/v1/codes-postaux/${pays}/`, { params: { q: texte } })
  return versSuggestions(reponse.data && reponse.data.resultats, Boolean(reponse.data && reponse.data.plus))
}

// null : l'annuaire ne couvre pas ce pays, l'écran interroge alors Google.
// Sans pays : aucune recherche (écrans où le pays est imposé, comme l'import).
export async function fournisseurCodesPostaux(texte, regions, client = axios) {
  const pays = (regions && regions[0]) || ''
  if (!pays) return []
  if (!PAYS_ANNUAIRE.includes(pays)) return null
  return chercher(pays, texte, client)
}

// Destinataire de l'écran de commande uniquement : sans pays, recherche dans les 6 pays
// de l'annuaire (« tous ») ; chaque ligne dit son pays, que le choix remplit (19/10/2026).
export async function fournisseurCodesPostauxDestination(texte, regions, client = axios) {
  if (regions && regions[0]) return fournisseurCodesPostaux(texte, regions, client)
  return chercher('tous', texte, client)
}
